import { activeSystemHistoryOwner, independentHistoryGeneration, preserveIndependentHistoryData, restoreSystemHistory, systemHistoryAvailable } from './system_history_service.mjs';
import { S } from '../app/app_state.js?v=20260817c';
import {
  appPerfEnd,
  appPerfStep,
  appPerfTimer
} from '../render/render_scheduler.js?v=20260706e';
import {
  PROJECT_STATE_MODES,
  normalizeProjectState,
  projectStateMode
} from '../project/project_state_service.js?v=20260904f';
import {
  collectProjectDrawingContentManifest,
  rememberProjectDrawingContentManifest
} from '../project/project_content_registry.mjs?v=20260904f';
import { markProjectModelChanged } from '../project/project_revision_service.js';
import { isPreparingProjectMutation } from '../project/project_mutation_scope.mjs';

function cloneDeep(obj) {
  if (typeof structuredClone === 'function') return structuredClone(obj);
  return JSON.parse(JSON.stringify(obj));
}

function cloneProjectForHistorySnapshot(project) {
  const source = project && typeof project === 'object' ? project : {};
  // Hintergrund/Grundriss (Bild/PDF/DXF) enthaelt oft eine mehrere MB grosse
  // Base64-URL und soll nicht in die Undo-Historie (Nutzerwunsch 2026-08-20)
  // -- wurde vorher bei JEDEM Snapshot mitkopiert, was in Projekten mit
  // Grundriss bei jeder Aktion spuerbar ruckelte. undoLast()/redoLast()
  // setzen den beim Aufruf aktuellen Hintergrund nach dem Restore deshalb
  // explizit wieder ein, statt ihn aus der Historie zu holen.
  // Materialisierte Systemdaten und abgeleitete Layouts
  // muessen dagegen Bestandteil des Snapshots bleiben. Ein verlustfreier
  // Hydrate-Restore darf sie nicht mit spaeteren Produktregeln rekonstruieren.
  const { floorplan: _floorplan, ...restProject } = source;
  return cloneDeep(restProject);
}

function projectSnapshot() {
  const mode = projectStateMode(S.project) === PROJECT_STATE_MODES.CREATE
    ? PROJECT_STATE_MODES.CREATE
    : PROJECT_STATE_MODES.HYDRATE;
  return {
    independentHistoryGeneration: independentHistoryGeneration(),
    project: cloneProjectForHistorySnapshot(S.project),
    selection: cloneDeep(S.selection),
    drawingContent: collectProjectDrawingContentManifest(S.project),
    // Der Modus lebt absichtlich ausserhalb des serialisierten Projekts. So
    // bleibt ein neues Projekt ueber Undo/Redo ein create-Projekt, waehrend
    // alte Snapshots ohne dieses Feld weiterhin konservativ hydrate nutzen.
    projectStateMode: mode
  };
}

function recordUndo(label = 'Änderung') {
  if (isPreparingProjectMutation()) throw new Error('Der Host besitzt die History dieser Projektmutation.');
  const profile = appPerfTimer('undo.record');
  if (S.file) S.file.dirty = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ks:project-dirty', { detail: { label } }));
  }
  const snapshot = projectSnapshot();
  appPerfStep(profile, 'snapshot');
  S.history.undo.push({
    label,
    snapshot
  });
  S.history.redo = [];

  if (S.history.undo.length > S.history.limit) {
    S.history.undo.shift();
  }
  markProjectModelChanged(label);
  appPerfEnd(profile, 16);
}

function stableHistoryProjectStateMode(snapshot) {
  return snapshot?.projectStateMode === PROJECT_STATE_MODES.CREATE
    ? PROJECT_STATE_MODES.CREATE
    : PROJECT_STATE_MODES.HYDRATE;
}

function normalizeAfterHistoryRestore(snapshot) {
  normalizeProjectState({
    mode: stableHistoryProjectStateMode(snapshot),
    source: 'history-restore'
  });
}

function undoLast() {
  const owner = activeSystemHistoryOwner();
  if (owner) return restoreSystemHistory(owner, 'undo');
  const entry = S.history.undo.pop();
  if (!entry) return false;

  S.history.redo.push({
    label: entry.label,
    snapshot: projectSnapshot()
  });
  if (S.history.redo.length > S.history.limit) {
    S.history.redo.shift();
  }

  const currentFloorplan = S.project?.floorplan ?? null;
  const restored = cloneDeep(entry.snapshot.project);
  preserveIndependentHistoryData(S.project, restored, entry.snapshot.independentHistoryGeneration);
  S.project = restored;
  rememberProjectDrawingContentManifest(S.project, entry.snapshot.drawingContent);
  S.project.floorplan = currentFloorplan;
  S.selection = cloneDeep(entry.snapshot.selection);
  normalizeAfterHistoryRestore(entry.snapshot);
  markProjectModelChanged(`undo:${entry.label}`);
  return true;
}

function redoLast() {
  const owner = activeSystemHistoryOwner();
  if (owner) return restoreSystemHistory(owner, 'redo');
  const entry = S.history.redo.pop();
  if (!entry) return false;

  S.history.undo.push({
    label: entry.label,
    snapshot: projectSnapshot()
  });
  if (S.history.undo.length > S.history.limit) {
    S.history.undo.shift();
  }

  const currentFloorplan = S.project?.floorplan ?? null;
  const restored = cloneDeep(entry.snapshot.project);
  preserveIndependentHistoryData(S.project, restored, entry.snapshot.independentHistoryGeneration);
  S.project = restored;
  rememberProjectDrawingContentManifest(S.project, entry.snapshot.drawingContent);
  S.project.floorplan = currentFloorplan;
  S.selection = cloneDeep(entry.snapshot.selection);
  normalizeAfterHistoryRestore(entry.snapshot);
  markProjectModelChanged(`redo:${entry.label}`);
  return true;
}

function canUndo() {
  const owner = activeSystemHistoryOwner();
  if (owner) return systemHistoryAvailable(owner, 'undo');
  return S.history.undo.length > 0;
}

function canRedo() {
  const owner = activeSystemHistoryOwner();
  if (owner) return systemHistoryAvailable(owner, 'redo');
  return S.history.redo.length > 0;
}

export {
  cloneDeep,
  cloneProjectForHistorySnapshot,
  projectSnapshot,
  recordUndo,
  undoLast,
  redoLast,
  canUndo,
  canRedo
};
